import { useEffect, useState } from "react";
import { Users as UsersIcon, Plus, Trash2, Save } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";

const ROLE_BADGE = {
  owner: "bg-violet-100 text-violet-700",
  developer: "bg-sky-100 text-sky-700",
};

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ email: "", password: "", name: "Admin", role: "developer" });

  const load = async () => {
    try { setItems((await api.get("/admin/users")).data); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/users", draft);
      toast.success("User created");
      setCreating(false);
      setDraft({ email: "", password: "", name: "Admin", role: "developer" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const updateField = (id, k, v) => setItems((prev) => prev.map((u) => (u.id === id ? { ...u, [k]: v } : u)));

  const save = async (u) => {
    try {
      const body = { name: u.name, role: u.role };
      if (u._new_password) body.password = u._new_password;
      await api.put(`/admin/users/${u.id}`, body);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-display font-bold text-[#0F172A] flex items-center gap-2">
            <UsersIcon className="w-6 h-6 text-[#1E3A8A]" /> Users
          </h2>
          <p className="text-sm text-[#475569] mt-1">Add owners or developers. Developers have read-only access to maintenance metrics only.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="admin-users-add" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#1E40AF]">
          <Plus className="w-4 h-4" /> Add user
        </button>
      </div>

      {creating && (
        <div className="mt-5 bg-white rounded-2xl border border-slate-100 p-5" data-testid="admin-users-new-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</span>
              <input data-testid="admin-users-new-email" type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password (min 8)</span>
              <input data-testid="admin-users-new-password" type="text" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</span>
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</span>
              <select data-testid="admin-users-new-role" value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm">
                <option value="developer">Developer</option>
                <option value="owner">Owner</option>
              </select>
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setCreating(false)} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold">Cancel</button>
            <button onClick={create} data-testid="admin-users-new-save" className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-semibold">Create</button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {items.map((u) => (
          <div key={u.id} className="bg-white rounded-2xl border border-slate-100 p-5 grid sm:grid-cols-12 gap-3 items-end" data-testid={`admin-users-row-${u.id}`}>
            <div className="sm:col-span-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</p>
              <p className="text-sm font-medium text-[#0F172A] break-all">{u.email}</p>
            </div>
            <label className="sm:col-span-3 block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</span>
              <input value={u.name || ""} onChange={(e) => updateField(u.id, "name", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="sm:col-span-2 block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</span>
              <select value={u.role} onChange={(e) => updateField(u.id, "role", e.target.value)} data-testid={`admin-users-row-${u.id}-role`} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
                <option value="developer">developer</option>
                <option value="owner">owner</option>
              </select>
            </label>
            <label className="sm:col-span-2 block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">New password</span>
              <input type="text" placeholder="(leave blank to keep)" value={u._new_password || ""} onChange={(e) => updateField(u.id, "_new_password", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <div className="sm:col-span-1 flex gap-2 justify-end">
              <button onClick={() => remove(u)} title="Delete" className="p-2 rounded-full bg-white border border-rose-200 text-rose-700 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
              <button onClick={() => save(u)} data-testid={`admin-users-row-${u.id}-save`} title="Save" className="p-2 rounded-full bg-[#1E3A8A] text-white hover:bg-[#1E40AF]"><Save className="w-4 h-4" /></button>
            </div>
            <span className={`absolute sm:static text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${ROLE_BADGE[u.role] || "bg-slate-100"}`}>{u.role}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
